import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@shared/lib';

export type ButtonVariant = 'primary' | 'ghost' | 'pill-dark' | 'outline' | 'naked' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-fg-inv hover:bg-accent-hi',
  ghost: 'bg-transparent text-fg border border-line hover:bg-elev',
  'pill-dark': 'bg-elev text-fg hover:bg-[#30303a]',
  // Заметная, но не оранжевая: светлая обводка без заливки — вторая по важности кнопка рядом с акцентной (плашки
  // главной, владелец 2026-10-22: «сделай обводку, белый фон не нужен»).
  outline: 'border border-solid border-fg/45 bg-transparent font-semibold text-fg hover:border-fg/80 hover:bg-white/[0.05]',
  naked: 'bg-transparent text-fg hover:text-accent',
  danger: 'bg-danger/15 text-danger border border-danger/30 hover:bg-danger/25',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3.5 py-2 text-[14px]',
  md: 'px-5 py-3 text-sm',
  lg: 'px-7 py-4 text-[15px]',
  xl: 'px-10 py-5 text-[17px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', fullWidth, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-pill font-medium tracking-[0.02em]',
        'transition-[background-color,color,border-color,transform] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-40',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    />
  ),
);

Button.displayName = 'Button';
